import { usePreviewStore } from '@/stores/preview-store'
import { resolveCitationTargetPage, toPrecisePreviewHighlight } from '@/lib/citation-preview'
import type { ResolvedCitation } from '@/types/tender-overview'
import { cn } from '@/lib/utils'

interface OverviewCitationChipProps {
  citation: ResolvedCitation
  className?: string
}

/**
 * Inline citation chip for the Tender Summary — a small iris superscript pill
 * matching the chat citation affordance. Clicking opens the source preview at
 * the cited page with the verbatim quote driving a precise text highlight.
 *
 * The `openPreview` call shape is copied verbatim from `MessageBubble`'s
 * `CitationButton` (quote-first `toPrecisePreviewHighlight` +
 * `resolveCitationTargetPage`) so the preview behaves identically to chat.
 */
export function OverviewCitationChip({ citation, className }: OverviewCitationChipProps) {
  const openPreview = usePreviewStore((state) => state.openPreview)

  const handleClick = () => {
    if (!citation.documentId || !citation.documentName) return

    const precise = toPrecisePreviewHighlight(
      citation.highlightData,
      citation.quote,
      `overview-${citation.documentId}-${citation.marker}`
    )

    const targetPage = resolveCitationTargetPage(
      precise?.highlight ? { highlight: precise.highlight } : undefined,
      citation.page
    )

    void openPreview({
      documentId: citation.documentId,
      documentName: citation.documentName,
      // Pin the context: this chip only ever renders inside TenderOverviewView,
      // an Analysis sub-tab, where the docked reader host is mounted. Without
      // this, a warm open (document already cached from another Analysis
      // citation click) would inherit the previous context via the store's
      // same-document fast path and dock instead of opening full-screen.
      context: 'analysis',
      ...(targetPage != null && { page: targetPage }),
      citationLabel: `Source [${citation.marker}]`,
      ...(precise?.highlight ? { highlight: precise.highlight } : {}),
      ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
    })
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'inline-flex items-center justify-center align-super mx-0.5 min-w-[15px] h-[15px] px-1',
        'rounded-full text-[9.5px] font-medium leading-none transition-colors cursor-pointer',
        'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand))] hover:bg-[hsl(var(--brand)/0.18)]',
        className
      )}
      aria-label={`Source ${citation.marker}: ${citation.documentName}`}
      title={citation.quote ? `"${citation.quote.slice(0, 120)}"` : citation.documentName}
    >
      {citation.marker}
    </button>
  )
}
